import { Editor } from "@tiptap/react";
import { saveAs } from "file-saver";
import store from "@/redux/store";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { parseChartForExport } from "../utils/chartExportUtils";
import { withoutLinkContext } from "@/services/citationDeepLink";

const exportToDOCX = async (
  editor: Editor,
  workspaceId: string,
  title?: string,
  includeLinks: boolean = true
) => {
  const {
    Document,
    Paragraph,
    TextRun,
    HeadingLevel,
    Table,
    TableRow,
    TableCell,
    Packer,
    AlignmentType,
    LevelFormat,
    ImageRun,
    WidthType,
    ExternalHyperlink,
    TableLayoutType,
  } = await import("docx");

  // Determine if we're exporting from a report or chat
  const isReport = window.location.pathname.startsWith("/reports/");
  const basePath = isReport ? "/reports" : "/chat";

  const sections = [];
  let numberingCounter = 1;

  const processTextWithMarks = (textNode: any, color?: string) => {
    let text = textNode.text;
    let textRunOptions: {
      text: any;
      bold?: boolean;
      italics?: boolean;
      color?: string;
    } = {
      text,
    };

    if (color) {
      textRunOptions.color = color;
    }

    if (textNode.marks) {
      textNode.marks.forEach((mark: any) => {
        switch (mark.type) {
          case "bold":
            textRunOptions.bold = true;
            break;
          case "italic":
            textRunOptions.italics = true;
            break;
          // Add more mark types as needed
        }
      });
    }

    return new TextRun(textRunOptions);
  };

  const processNestedContent = (
    content: any,
    textColor?: string,
    prevNode?: any
  ): any[] => {
    if (!content) return [];
    const processedContent = content
      .map((node: any, index: number) => {
        if (!node) return null;

        let shouldRemovePrecedingSpace = false;
        // Only strip spaces before nodes that are truly removed from output:
        // - citation nodes are always removed
        // - query-info nodes are removed when includeLinks is false
        const shouldCheckForPunctuation =
          node.type === "citation" ||
          (node.type === "query-info" && !includeLinks);

        if (shouldCheckForPunctuation && index > 0) {
          const prev = content[index - 1];
          // Check if previous node is text ending with space
          const prevHasTrailingSpace =
            prev &&
            prev.type === "text" &&
            prev.text &&
            prev.text.endsWith(" ");

          // Check what comes after the citation
          const next = index < content.length - 1 ? content[index + 1] : null;
          const nextText = next && next.type === "text" ? next.text : "";

          // Check if citation is followed by punctuation (period, comma, etc.)
          const isFollowedByPunctuation =
            nextText && /^[.,;:!?)\]}]/.test(nextText.trim());

          // Check if next text starts with space (would create double space)
          const nextStartsWithSpace = nextText && /^\s/.test(nextText);

          // Remove space if citation is followed by punctuation
          // Also check for double space: if prev ends with space AND next starts with space,
          // we should remove the preceding space to avoid double space
          if (
            prevHasTrailingSpace &&
            (isFollowedByPunctuation || nextStartsWithSpace)
          ) {
            shouldRemovePrecedingSpace = true;
          }
        }

        if (
          node.content &&
          node.type !== "editor-highlight-number" &&
          node.type !== "numeric-citation"
        ) {
          return processNestedContent(node.content, textColor, node);
        }
        switch (node.type) {
          case "text":
            let text = node.text;
            // Remove trailing space if next node is a citation
            // Citation nodes are always removed, other types only when includeLinks is false
            if (index < content.length - 1) {
              const next = content[index + 1];
              // Only strip spaces before nodes that are truly removed from output
              const shouldCheck =
                next?.type === "citation" ||
                (next?.type === "query-info" && !includeLinks);

              if (shouldCheck) {
                // Check what comes after the citation
                const afterCitation =
                  index + 1 < content.length - 1 ? content[index + 2] : null;
                const afterCitationText =
                  afterCitation && afterCitation.type === "text"
                    ? afterCitation.text
                    : "";

                // Check if citation is followed by punctuation
                const isFollowedByPunctuation =
                  afterCitationText &&
                  /^[.,;:!?)\]}]/.test(afterCitationText.trim());

                // Check if text after citation starts with space (would create double space)
                const afterCitationStartsWithSpace =
                  afterCitationText && /^\s/.test(afterCitationText);

                // Remove trailing space if citation is followed by punctuation or would create double space
                if (isFollowedByPunctuation || afterCitationStartsWithSpace) {
                  text = text.replace(/\s+$/, "");
                }
              }
            }
            // Also handle case where this text has trailing space and should be removed
            if (shouldRemovePrecedingSpace && text.endsWith(" ")) {
              text = text.slice(0, -1);
            }

            // Check if this text node has a link mark (for citation links from markdown)
            if (node.marks) {
              const linkMark = node.marks.find(
                (mark: any) => mark.type === "link"
              );
              if (linkMark && linkMark.attrs?.href) {
                // A link leaving the app carries no passage text (#14039).
                let href = withoutLinkContext(linkMark.attrs.href);
                // When links are disabled, return plain text — but preserve "View Excel Spreadsheet" links
                if (!includeLinks && !text.includes("View Excel Spreadsheet")) {
                  return processTextWithMarks({ ...node, text }, textColor);
                }
                // Convert relative URL to absolute if needed
                if (href.startsWith("/")) {
                  href = `${window.location.origin}${href}`;
                } else if (
                  !href.startsWith("http://") &&
                  !href.startsWith("https://")
                ) {
                  // If it's a relative path without leading slash, prepend base path
                  href = `${window.location.origin}${basePath}/${workspaceId}${href.startsWith("?") ? "" : "/"}${href}`;
                }
                return new ExternalHyperlink({
                  children: [
                    processTextWithMarks({ ...node, text }, "#3B82F6"),
                  ],
                  link: href,
                });
              }
            }

            return processTextWithMarks({ ...node, text }, textColor);
          case "numeric-citation": {
            const numValue = (node.content || [])
              .map((child: any) => child.text || "")
              .join("");
            if (!includeLinks) {
              // When links are disabled, return plain text instead of removing
              return new TextRun({ text: numValue });
            }
            const ncQid = node.attrs?.qid;
            const ncCids = node.attrs?.cids;
            const ncMatch = node.attrs?.match;
            let ncLink = `${window.location.origin}${basePath}/${workspaceId}`;
            if (ncQid && ncCids) {
              ncLink += `?qid=${ncQid}&cid=${ncCids}&match=${encodeURIComponent(ncMatch || "")}`;
            }
            return new ExternalHyperlink({
              children: [new TextRun({ text: numValue, color: "#3B82F6" })],
              link: ncLink,
            });
          }
          case "editor-highlight-number": {
            // Legacy numeric citation.
            if (node.attrs && node.attrs.numValue) {
              const numValue =
                node.attrs.originalNumValue || node.attrs.numValue?.toString();
              if (!includeLinks) {
                // When links are disabled, return plain text instead of removing
                return new TextRun({ text: numValue });
              }
              const ehQid = node.attrs.qid;
              const ehCid = node.attrs.numValue?.toString();
              const ehMatch = node.attrs.originalNumValue || numValue;
              let ehLink = `${window.location.origin}${basePath}/${workspaceId}`;
              if (ehQid && ehCid) {
                ehLink += `?qid=${ehQid}&cid=${ehCid}&match=${encodeURIComponent(ehMatch || "")}`;
              }
              return new ExternalHyperlink({
                children: [new TextRun({ text: numValue, color: "#3B82F6" })],
                link: ehLink,
              });
            }
            return null;
          }
          case "citation":
            // Citation nodes are always removed (they're just visual markers)
            return null;
          case "query-info":
            if (!includeLinks) return null;
            // Handle query info icon similar to PDF export
            const infoText = new TextRun({
              text: "ℹ️", // Info emoji as a simple representation of the icon
              bold: true,
              color: "3B82F6", // Same blue color as SVG
            });
            return new ExternalHyperlink({
              children: [infoText],
              link: `${window.location.origin}${basePath}/${workspaceId}?show_query=${node.attrs?.qid}`,
            });
          default:
            console.warn(`Unhandled node type: ${node.type}`);
            return null;
        }
      })
      .flatMap((node: any) => (Array.isArray(node) ? node : [node]))
      .filter((item: any) => item !== null);
    return processedContent;
  };

  const processList = (node: any, level = 0) => {
    let numberingReference = `numbered-list-${numberingCounter++}`;
    return node.content
      .map((listItem: any) => {
        if (!listItem.content) return null;
        return listItem.content.map((block: any, blockIdx: number) => {
          if (block.type === "bulletList" || block.type === "orderedList") {
            return processList(block, level + 1);
          }
          const children = processNestedContent(block.content, undefined);
          if (blockIdx === 0) {
            return new Paragraph({
              children,
              bullet: node.type === "bulletList" ? { level } : undefined,
              numbering:
                node.type === "orderedList"
                  ? { reference: numberingReference, level }
                  : undefined,
            });
          } else {
            return new Paragraph({
              children,
              indent: { left: 720 * (level + 1) },
            });
          }
        });
      })
      .flat(2)
      .filter(Boolean);
  };

  const captureChartImage = async (chartElement: HTMLElement) => {
    if (!chartElement) return null;

    const legendElements = chartElement.querySelectorAll(
      '[elementType="legend"]'
    );
    const originalStyles: {
      element: HTMLElement;
      backgroundColor: string;
      border: string;
      borderLeft: string;
    }[] = [];

    legendElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      originalStyles.push({
        element: htmlEl,
        backgroundColor: htmlEl.style.backgroundColor,
        border: htmlEl.style.border,
        borderLeft: htmlEl.style.borderLeft,
      });
      htmlEl.style.backgroundColor = "transparent";
      htmlEl.style.border = "none";
      htmlEl.style.borderLeft = "none";
    });

    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(chartElement, {
      scale: 3,
      useCORS: true,
      logging: true,
    });

    originalStyles.forEach((style) => {
      style.element.style.backgroundColor = style.backgroundColor;
      style.element.style.border = style.border;
      style.element.style.borderLeft = style.borderLeft;
    });

    return canvas.toDataURL("image/png");
  };

  const processNode = async (node: any, editor?: Editor) => {
    switch (node.type) {
      case "paragraph":
        if (!node.content || node.content.length === 0) {
          return new Paragraph({});
        }
        return new Paragraph({
          children: processNestedContent(node.content, undefined),
        });

      case "heading":
        if (!node.content) return null;
        const level = node.attrs.level;
        return new Paragraph({
          children: processNestedContent(node.content, undefined),
          heading:
            HeadingLevel[`HEADING_${level}` as keyof typeof HeadingLevel],
        });

      case "bulletList":
      case "orderedList":
        if (!node.content) return null;
        return processList(node);

      case "table": {
        if (!node.content) return null;
        // Count columns from the first row, accounting for colspan
        const firstRow = node.content[0];
        const numColumns =
          firstRow?.content?.reduce(
            (sum: number, cell: any) => sum + (cell.attrs?.colspan || 1),
            0
          ) || 1;
        // 9360 DXA = usable width of a standard letter page
        const columnWidthDxa = Math.floor(9360 / numColumns);

        const rows = node.content.map(
          (row: any, rowIndex: number) =>
            new TableRow({
              children: row.content.map((cell: any) => {
                const colspan = cell.attrs?.colspan || 1;
                const rowspan = cell.attrs?.rowspan || 1;
                return new TableCell({
                  children: cell.content.map(
                    (p: any) =>
                      new Paragraph({
                        children: processNestedContent(
                          p.content,
                          rowIndex === 0 ? "FFFFFF" : undefined
                        ),
                      })
                  ),
                  width: {
                    size: Math.round((colspan / numColumns) * 100),
                    type: WidthType.PERCENTAGE,
                  },
                  columnSpan: colspan > 1 ? colspan : undefined,
                  rowSpan: rowspan > 1 ? rowspan : undefined,
                  shading: {
                    fill:
                      rowIndex === 0
                        ? "555555"
                        : rowIndex % 2 === 0
                          ? "F0F0F0"
                          : "FFFFFF",
                  },
                });
              }),
            })
        );
        const table = new Table({
          rows,
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
          layout: TableLayoutType.FIXED,
          columnWidths: Array(numColumns).fill(columnWidthDxa),
        });

        return table;
      }

      case "hardBreak":
        return new Paragraph({});

      case "queryTextBox":
        if (!node.content) return null;
        return new Paragraph({
          children: processNestedContent([
            {
              text: node.attrs.text,
              type: "text",
            },
          ]),
          shading: {
            fill: "F0F0F0",
          },
        });

      case "chart-node": {
        // Handle chart-node type (current chart implementation)
        const { qid, id: chartId } = node.attrs || {};
        if (!qid || !chartId) {
          console.warn("Chart node missing qid or id attributes");
          return null;
        }

        try {
          // Get chart data from Redux state
          const queryAnswers = store.getState().fpAppV4Data.queryAnswers || [];
          const queryAnswer = queryAnswers.find((qa: any) => qa.qid === qid);
          let chartData =
            queryAnswer?.toolResponse?.answer?.chartData?.[chartId];

          if (!chartData) {
            console.warn(
              `Chart data not found for qid: ${qid}, chartId: ${chartId}`
            );
            return null;
          }

          const processedChartData = parseChartForExport(chartData);
          if (
            !processedChartData ||
            typeof processedChartData !== "object" ||
            Array.isArray(processedChartData)
          ) {
            console.warn("Chart data could not be parsed for export");
            return null;
          }

          // node.attrs.width is a percentage (e.g. 60 = 60%) — convert to pixels
          // for the render API. Use 800px as the full-width reference.
          const FULL_WIDTH_PX = 800;
          const renderWidth = node.attrs.width
            ? Math.round((node.attrs.width / 100) * FULL_WIDTH_PX)
            : FULL_WIDTH_PX;
          // When aspect ratio is set, compute height from it (overrides default)
          const config = processedChartData?.charts?.[0] ?? processedChartData;
          const ar = config?.options?.aspectRatio;
          let renderHeight: number;
          if (ar != null) {
            renderHeight = Math.round(renderWidth / ar);
          } else if (node.attrs.height != null) {
            renderHeight = node.attrs.height;
          } else {
            renderHeight = 600;
          }

          // Use chart renderer API to convert chart to image
          const response = await fpFetch(
            `${BASE_API_URL_V2}/api/v2/chat/render-chart`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                chart: processedChartData,
                width: renderWidth,
                height: renderHeight,
              }),
            }
          );

          if (!response.ok) {
            console.warn(`Failed to render chart: ${response.statusText}`);
            return null;
          }

          const renderResult = await response.json();
          const imageBase64 = renderResult.imageBase64;
          const originalWidth = renderResult.originalWidth || 800;
          const originalHeight = renderResult.originalHeight || 600;

          if (!imageBase64) {
            console.warn("Chart renderer returned no image data");
            return null;
          }

          // Calculate dimensions while preserving aspect ratio
          // Max dimensions to fit on Word page (similar to PDF export which uses original dimensions)
          // Use reasonable max size: 600px width, but scale height proportionally
          const maxWidth = 600;
          const maxHeight = 450;

          // Calculate aspect ratio from original dimensions
          const aspectRatio = originalWidth / originalHeight;

          // Scale to fit within max dimensions while preserving aspect ratio
          let finalWidth = originalWidth;
          let finalHeight = originalHeight;

          // If width exceeds max, scale down proportionally
          if (finalWidth > maxWidth) {
            finalWidth = maxWidth;
            finalHeight = finalWidth / aspectRatio;
          }

          // If height still exceeds max after width scaling, scale down by height
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = finalHeight * aspectRatio;
          }

          // Use pixels for dimensions (docx library handles conversion internally)
          // This matches the existing MarketData case which uses pixels directly
          const imageRun = new ImageRun({
            data: imageBase64,
            transformation: {
              width: Math.round(finalWidth),
              height: Math.round(finalHeight),
            },
            type: "png",
          });

          // Map chart alignment to DOCX paragraph alignment
          const chartAlign = node.attrs.align;
          let paragraphAlignment;
          if (chartAlign === "left") {
            paragraphAlignment = AlignmentType.LEFT;
          } else if (chartAlign === "right") {
            paragraphAlignment = AlignmentType.RIGHT;
          } else {
            paragraphAlignment = AlignmentType.CENTER;
          }

          return new Paragraph({
            children: [imageRun],
            alignment: paragraphAlignment,
          });
        } catch (error) {
          console.error("Error rendering chart for Word export:", error);
          return null;
        }
      }

      case "MarketData":
        // Legacy MarketData node type - try to capture from DOM as fallback
        const chartElement = document.querySelector(
          `[data-qid="${node.attrs.qid}"]`
        );
        if (chartElement) {
          const chartImage = await captureChartImage(
            chartElement as HTMLElement
          );
          if (chartImage) {
            const imageRun = new ImageRun({
              data: chartImage.split(",")[1],
              transformation: {
                width: 600,
                height: 350,
              },
              type: "png",
            });
            return new Paragraph({
              children: [imageRun],
            });
          } else {
            console.warn("Failed to capture chart image");
          }
        } else {
          console.warn("Chart element not found for MarketData node");
        }
        return null;

      default:
        console.warn(`Unhandled node type: ${node.type}`);
        return null;
    }
  };

  const content = editor.getJSON();
  for (const node of content?.content || []) {
    const processed = await processNode(node, editor);
    if (Array.isArray(processed)) {
      sections.push(...processed);
    } else if (processed) {
      sections.push(processed);
    }
  }

  const doc = new Document({
    numbering: {
      config: [
        ...Array.from({ length: numberingCounter }, (_, i) => ({
          reference: `numbered-list-${i + 1}`,
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: "%1.",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: { left: 720, hanging: 260 },
                },
              },
            },
            {
              level: 1,
              format: LevelFormat.DECIMAL,
              text: "%2.",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: { left: 1440, hanging: 520 },
                },
              },
            },
            // Add more levels as needed
          ],
        })),
      ],
    },
    sections: [
      {
        children: sections,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const filename = title
    ? `${title.replace(/[^a-z0-9]/gi, "-").toLowerCase()}.docx`
    : "finpilot-note.docx";
  saveAs(blob, filename);
};

export default exportToDOCX;
